import { createContext, useContext, useMemo, useState, ReactNode } from 'react';

export interface EditorActiveNode {
  uuid?: string;
  type?: string;
  formatting?: { format?: string; class?: string };
}

export interface EditorActiveSection {
  uuid?: string;
  type?: string;
  templateSuperId?: string;
  pageId?: string;
}

export interface EditorLiveSection {
  superId: string;
  revisionId: string;
  content: Array<{ uuid?: string; [key: string]: unknown }>;
  documentSuperId: string | number;
  [key: string]: unknown;
}

const EMPTY_LIVE_SECTION: EditorLiveSection = { superId: '', revisionId: '', content: [], documentSuperId: '' };

export interface EditorCaretState {
  activeNode: EditorActiveNode;
  selectionId: string | null;
}

export interface EditorCoarseState {
  activeSection: EditorActiveSection;
  isDirty: boolean;
}

interface EditorUiSetters {
  setActiveNode: (node: EditorActiveNode) => void;
  setSelectionId: (id: string | null) => void;
  setActiveSection: (section: EditorActiveSection) => void;
  setIsDirty: (dirty: boolean) => void;
}

const CARET_DEFAULT: EditorCaretState = { activeNode: {}, selectionId: null };
const COARSE_DEFAULT: EditorCoarseState = { activeSection: {}, isDirty: false };
const NO_SETTERS: EditorUiSetters = {
  setActiveNode: () => {},
  setSelectionId: () => {},
  setActiveSection: () => {},
  setIsDirty: () => {},
};

const EditorSectionContext = createContext<EditorLiveSection>(EMPTY_LIVE_SECTION);

const EditorCaretContext = createContext<EditorCaretState>(CARET_DEFAULT);
const EditorCoarseContext = createContext<EditorCoarseState>(COARSE_DEFAULT);
const EditorUiSettersContext = createContext<EditorUiSetters>(NO_SETTERS);

export const EditorSectionProvider = ({ section, children }: { section: EditorLiveSection | null | undefined; children: ReactNode }) => (
  <EditorSectionContext.Provider value={section ?? EMPTY_LIVE_SECTION}>{children}</EditorSectionContext.Provider>
);

export const useEditorLiveSection = () => useContext(EditorSectionContext);

export const EditorUiProvider = ({ children }: { children: ReactNode }) => {
  const [activeNode, setActiveNode] = useState<EditorActiveNode>({});
  const [selectionId, setSelectionId] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState<EditorActiveSection>({});
  const [isDirty, setIsDirty] = useState(false);

  const caret = useMemo(() => ({ activeNode, selectionId }), [activeNode, selectionId]);
  const coarse = useMemo(() => ({ activeSection, isDirty }), [activeSection, isDirty]);
  const setters = useMemo(() => ({ setActiveNode, setSelectionId, setActiveSection, setIsDirty }), []);

  return (
    <EditorUiSettersContext.Provider value={setters}>
      <EditorCoarseContext.Provider value={coarse}>
        <EditorCaretContext.Provider value={caret}>{children}</EditorCaretContext.Provider>
      </EditorCoarseContext.Provider>
    </EditorUiSettersContext.Provider>
  );
};

export const useEditorCaret = () => useContext(EditorCaretContext);
export const useEditorCoarse = () => useContext(EditorCoarseContext);
export const useEditorUiSetters = () => useContext(EditorUiSettersContext);
